import { brand } from '@/config/brands';
import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import Dashboard from '@/pages/DashboardPage.vue';
import HomePage from '@/pages/HomePage.vue';
import ListOrdersPage from '@/pages/orders/ListOrdersPage.vue';
import CreateOrderPage from '@/pages/orders/CreateOrderPageNew.vue';
import DetailOrderPage from '@/pages/orders/DetailsOrderPage.vue';
import OrderInfoTab from '@/components/orders/details/OrderInfoTab.vue';
import OrderSettingsTab from '@/components/orders/details/OrderSettingsTab.vue';
import OrderPackagesTab from '@/components/orders/details/OrderPackagesTab.vue';
import OrderPreviewTab from '@/components/orders/details/OrderPreviewTab.vue';
import OrderSupportTab from '@/components/orders/details/OrderSupportTab.vue';
import OrderJobPostingTab from '@/components/orders/details/OrderJobPostingTab.vue';
// temporarily hidden
// import OrderStatsTab from '../pages/orders/details/OrderStatsTab.vue';
import OrderKanbanTab from '@/components/orders/details/OrderKanbanTab.vue';
import PoolmirrorDetailPage from '@/pages/poolmirror/PoolmirrorDetailPage.vue';
import PoolmirrorListPage from '@/pages/poolmirror/PoolmirrorListPage.vue';
import ListCVPage from '@/pages/cvanalyzer/ListCVPage.vue';
import ExternalJobMarketListPage from '@/pages/externalJobMarket/ExternalJobMarketListPage.vue';

import AppsListPage from '@/pages/promptsuite/AppsListPage.vue';
import AppDetailPage from '@/pages/promptsuite/AppDetailPage.vue';

import ListAccountsPageVue from '@/pages/settings/account/ListAccountsPage.vue';
import AccountDetailPage from '@/pages/settings/account/AccountDetailPage.vue';
import SettingsOverviewPage from '@/pages/settings/SettingsOverviewPage.vue';
import SwimlaneSettingsPage from '@/pages/settings/SwimlaneSettingsPage.vue';
import CandidateFieldsSettingsPage from '@/pages/settings/CandidateFieldsSettingsPage.vue';

import BillingBudgetPage from '@/pages/billing/BillingBudgetPage.vue';
import BillingVirtualBudgetsPage from '@/pages/billing/BillingVirtualBudgetsPage.vue';

import ListCompaniesPage from '@/pages/settings/company/ListCompaniesPage.vue';
import CompanyDetailsPage from '@/pages/settings/company/CompanyDetailsPage.vue';

import ListNetworksPage from '@/pages/settings/networks/ListNetworksPage.vue';

import Login from '@/pages/LoginPage.vue';
import ForgotPassword from '@/pages/ForgotPassword.vue';
import ResetPassword from '@/pages/ResetPassword.vue';
import RegistrationPage from '@/pages/registration/RegistrationPage.vue';
import SetPasswordPage from '@/pages/registration/SetPasswordPage.vue';
import SSOCallbackPage from '@/pages/SSOCallbackPage.vue';
import SSOLinkPage from '@/pages/SSOLinkPage.vue';
import BillingBudgetSplitsPage from '@/pages/billing/BillingBudgetSplitsPage.vue';
import OrderBannersTab from '@/components/orders/details/OrderBannersTab.vue';
import BannerEditor from '@/components/banners/BannerEditor.vue';
import { useUserStore } from '@/stores/user';
import { useSelfRegistrationSetupStatus } from '@/composables/useSelfRegistrationSetupStatus';
import { allowSelfRegistration } from '@/config/app';
import CandidateDetailPage from '@/pages/candidate/CandidateDetailPage.vue';
import CandidateChatPage from '@/pages/candidate/CandidateChatPage.vue';
import CandidateUploadPage from '@/pages/candidate/CandidateUploadPage.vue';
import SignatureSettingsPage from '@/pages/settings/SignatureSettingsPage.vue';
import EntityRedirectPage from '@/pages/EntityRedirectPage.vue';

const routes = <Array<RouteRecordRaw>>[
  {
    path: '/',
    redirect: { name: 'home' },
  },
  {
    path: '/dashboard/home',
    component: Dashboard,
    // All pages should go as children to this component (they would inherit the layout of the Dashboard), and the login page is separate
    children: [
      {
        path: '/dashboard/home',
        name: 'home',
        component: HomePage,
      },
      {
        path: '/dashboard/orders/:company_name?',
        name: 'orders.list',
        component: ListOrdersPage,
      },
      {
        path: '/dashboard/orders/create',
        name: 'order.create',
        component: CreateOrderPage,
      },
      {
        path: '/dashboard/orders/detail/:id',
        name: 'order.detail',
        component: DetailOrderPage,
        children: [
          {
            path: '',
            redirect: { name: 'order.detail.info' },
          },
          {
            path: '/dashboard/orders/detail/:id/info',
            name: 'order.detail.info',
            component: OrderInfoTab,
          },
          {
            path: '/dashboard/orders/detail/:id/settings',
            name: 'order.detail.settings',
            component: OrderSettingsTab,
          },
          {
            path: '/dashboard/orders/detail/:id/packages',
            name: 'order.detail.packages',
            component: OrderPackagesTab,
          },
          {
            path: '/dashboard/orders/detail/:id/banners',
            name: 'order.detail.banners',
            component: OrderBannersTab,
          },
          {
            path: '/dashboard/orders/detail/:id/preview',
            name: 'order.detail.preview',
            component: OrderPreviewTab,
          },
          {
            path: '/dashboard/orders/detail/:id/support',
            name: 'order.detail.support',
            component: OrderSupportTab,
          },
          {
            path: '/dashboard/orders/detail/:id/job_posting',
            name: 'order.detail.job_posting',
            component: OrderJobPostingTab,
          },
          // temporarily hidden
          // {
          //   path: '/dashboard/orders/detail/:id/stats',
          //   name: 'order.detail.stats',
          //   component: OrderStatsTab,
          // },
          {
            path: '/dashboard/orders/detail/:id/kanban',
            name: 'order.detail.kanban',
            component: OrderKanbanTab,
          },
        ],
      },
      {
        path: '/dashboard/banners/sandbox',
        name: 'banners.sandbox',
        component: BannerEditor,
      },
      {
        path: '/dashboard/poolmirror',
        name: 'poolmirror.list',
        component: PoolmirrorListPage,
      },
      {
        path: '/dashboard/poolmirror/:id/detail',
        name: 'poolmirror.detail',
        component: PoolmirrorDetailPage,
      },
      {
        path: '/dashboard/cvanlayzer',
        name: 'cvanalyzer.list',
        component: ListCVPage,
      },
      {
        path: '/dashboard/external-job-markets/:datapoolId?',
        name: 'external_job_markets.list',
        component: ExternalJobMarketListPage,
      },
      {
        path: '/dashboard/promptsuite/apps',
        name: 'promptsuite.apps',
        component: AppsListPage,
      },
      {
        path: '/dashboard/promptsuite/apps/:id',
        name: 'promptsuite.app.detail',
        component: AppDetailPage,
      },
      {
        path: '/dashboard/billing',
        children: [
          {
            path: '/dashboard/billing',
            name: 'billing',
            redirect: { name: 'billing.budget' },
          },
          {
            path: '/dashboard/billing/budget',
            name: 'billing.budget',
            component: BillingBudgetPage,
          },
          {
            path: '/dashboard/billing/budget/splits',
            name: 'billing.splits',
            component: BillingBudgetSplitsPage,
          },
          {
            path: '/dashboard/billing/vbudgets',
            name: 'billing.vbudgets',
            component: BillingVirtualBudgetsPage,
          },
        ],
      },
      {
        path: '/dashboard/settings',
        name: 'settings',
        component: SettingsOverviewPage,
      },
      {
        path: '/dashboard/settings/accounts',
        name: 'settings.accounts',
        component: ListAccountsPageVue,
      },
      {
        path: '/dashboard/settings/accounts/:id',
        name: 'settings.accounts.detail',
        component: AccountDetailPage,
      },
      {
        path: '/dashboard/settings/companies',
        name: 'settings.companies',
        component: ListCompaniesPage,
        children: [
          {
            path: '/dashboard/settings/companies/:id',
            name: 'settings.company.details',
            component: CompanyDetailsPage,
          },
        ],
      },
      {
        path: '/dashboard/settings/networks',
        name: 'settings.networks',
        component: ListNetworksPage,
      },
      {
        path: '/dashboard/settings/swimlanes',
        name: 'settings.swimlanes',
        component: SwimlaneSettingsPage,
      },
      {
        path: '/dashboard/settings/candidate-fields',
        name: 'settings.candidate_fields',
        component: CandidateFieldsSettingsPage,
      },
      {
        path: '/dashboard/settings/signature',
        name: 'settings.signature',
        component: SignatureSettingsPage,
      },
      {
        path: '/dashboard/candidate/:id',
        name: 'candidate.detail',
        component: CandidateDetailPage,
      },
      {
        path: '/jump/:type/:info(.*)?',
        name: 'entity.redirect',
        component: EntityRedirectPage,
        props: true,
      },
    ],
  },
  {
    name: 'form.upload',
    path: '/form/upload/:token',
    component: CandidateUploadPage,
    meta: { requiresAuth: false },
  },
  {
    name: 'form.reply',
    path: '/form/reply/:token',
    component: CandidateChatPage,
    meta: { requiresAuth: false },
  },
  {
    name: 'login',
    path: '/login',
    component: Login,
    meta: { requiresAuth: false, redirectAuthenticatedToHome: true },
  },
  {
    // Google and Microsoft redirect here; registered as a redirect URI per LEAD host
    name: 'sso.callback',
    path: '/sso/:provider/callback',
    component: SSOCallbackPage,
    meta: { requiresAuth: false },
  },
  {
    // confirms the code sent to an existing account before a Google or Microsoft identity is linked to it
    name: 'sso.link',
    path: '/sso/link',
    component: SSOLinkPage,
    meta: { requiresAuth: false, redirectAuthenticatedToHome: true },
  },
  {
    name: 'forgotPassword',
    path: '/forgot_password',
    component: ForgotPassword,
    meta: { requiresAuth: false },
  },
  {
    name: 'resetPassword',
    path: '/reset_password',
    component: ResetPassword,
    meta: { requiresAuth: false, redirectAuthenticatedToHome: true },
  },
  {
    name: 'registration',
    path: '/register',
    component: RegistrationPage,
    // public without redirectAuthenticatedToHome: the registrant is signed in from email verification onward and must stay in the wizard
    meta: { requiresAuth: false },
  },
  {
    name: 'registration.setPassword',
    path: '/register/password',
    component: SetPasswordPage,
    // must survive the unfinished-registration guard below, which would otherwise bounce it back into the wizard
    meta: { requiresAuth: true, availableWhileUnfinished: true },
  },
];

// routes require a session unless they opt out with `requiresAuth: false`, so new routes are private by default
const addAuth = (el: RouteRecordRaw): void => {
  el.meta = el.meta ?? {};

  if (el.meta.requiresAuth === undefined) {
    el.meta.requiresAuth = true;
  }

  if (Array.isArray(el.children)) {
    el.children.forEach((child) => {
      addAuth(child);
    });
  }
};

routes.forEach((el) => {
  addAuth(el);
});

const router = createRouter({
  history: createWebHistory(),
  linkExactActiveClass: 'active router-link-active',
  routes,
});

router.beforeEach(async (to) => {
  document.title = to.meta.title ? `${to.meta.title} - ${brand.name}` : brand.name;

  const userStore = useUserStore();

  // every guard below reads the resolved session, so no navigation proceeds before it. a failed fetch is surfaced by
  // App.vue, which keeps the router view unmounted
  try {
    await userStore.ensureResolved();
  } catch {
    return true;
  }

  // guard: a session-requiring route is unreachable without one
  if (to.meta.requiresAuth && userStore.hasResolvedSession && !userStore.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  // guard: sign-in/reset-password pages are for strangers and unfinished registrants belong in the wizard, so both leave for
  // `authenticatedLandingRoute()`. mirrors App.vue's init checks for client-side navigation.
  // `availableWhileUnfinished` exempts the mid-registration "set your password" screen
  if (
    userStore.hasResolvedSession &&
    userStore.isAuthenticated &&
    (Boolean(to.meta.redirectAuthenticatedToHome) ||
      (Boolean(to.meta.requiresAuth) && userStore.hasUnfinishedRegistration && !to.meta.availableWhileUnfinished))
  ) {
    return userStore.authenticatedLandingRoute();
  }

  // guard: self-registration is appiq-only. the wizard route is `requiresAuth: false`, so it is otherwise reachable from any build.
  // waits for the resolved session so an already-authenticated unfinished registrant is not bounced; App.vue covers the initial load
  if (
    to.name === 'registration' &&
    !allowSelfRegistration &&
    userStore.hasResolvedSession &&
    !userStore.isAuthenticated
  ) {
    return { name: 'login' };
  }

  // guard: a finished registrant must not re-enter the wizard or "set your password" screen. separate from the generic
  // guard above, which skips `requiresAuth: false` routes (the wizard) and routes exempt via `availableWhileUnfinished`
  if (
    (to.name === 'registration' || to.name === 'registration.setPassword') &&
    userStore.hasResolvedSession &&
    userStore.isAuthenticated &&
    !userStore.hasUnfinishedRegistration
  ) {
    return { name: 'home' };
  }

  // guard: prevent non-admin users from accessing the account creation page
  if (to.name === 'settings.accounts.detail' && to.params.id === '+') {
    if (!userStore.currentUser) {
      await userStore.fetchCurrent();
    }

    if (!userStore.isLoggedIn) {
      return { name: 'login' };
    }
    if (!userStore.currentUser?.is_admin) {
      return { name: 'settings.accounts' };
    }
  }

  // guard: a self-registered customer whose registration defaults failed to auto-configure
  // cannot start a new order - single choke point here covers every entry point. Only a
  // `required_for_order_creation` failure blocks; a non-required one (e.g. stats selection
  // cards) never lands here.
  if (to.name === 'order.create') {
    if (userStore.isAuthenticated && useSelfRegistrationSetupStatus().isOrderCreationBlocked.value) {
      return { name: 'home' };
    }
  }
});

export default router;
